import { useCallback, useEffect, useState } from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import './App.css';
import LandingScreen from './features/landing/screens/LandingScreen';
import SplashScreen from './features/splash/screens/SplashScreen';
import AuthScreen from './features/auth/screens/AuthScreen';
import DashboardScreen from './features/dashboard/screens/DashboardScreen';
import AviatorScreen from './features/dashboard/screens/AviatorScreen';
import FairAviatorScreen from './features/dashboard/screens/FairAviatorScreen';
import FairAviatorLobbyScreen from './features/dashboard/screens/FairAviatorLobbyScreen';
import WinGoScreen from './features/dashboard/screens/WinGoScreen';
import LuckyDrawScreen from './features/dashboard/screens/LuckyDrawScreen';
import MatkaScreen from './features/dashboard/screens/MatkaScreen';
import MinesScreen from './features/dashboard/screens/MinesScreen';
import PlinkoScreen from './features/dashboard/screens/PlinkoScreen';
import LimboScreen from './features/dashboard/screens/LimboScreen';
import LudoLobbyScreen from './features/ludo/LudoLobbyScreen';
import LudoPvPScreen from './features/ludo/LudoPvPScreen';
// Phase 2 — enable when Dragon Tiger goes live
// import DragonTigerScreen from './features/dashboard/screens/DragonTigerScreen';
import AccountScreen from './features/account/screens/AccountScreen';
import WalletScreen from './features/account/screens/WalletScreen';
import DepositScreen from './features/account/screens/DepositScreen';
import UpiPaymentScreen from './features/account/screens/UpiPaymentScreen';
import WithdrawScreen from './features/account/screens/WithdrawScreen';
import AddBankAccountScreen from './features/account/screens/AddBankAccountScreen';
import BankAccountsScreen from './features/account/screens/BankAccountsScreen';
import ContactUsScreen from './features/account/screens/ContactUsScreen';
import { authApi } from './services/api/authApi';
import { walletApi } from './services/api/walletApi';
import { resetSocket } from './services/api/gamesApi';
import { captureReferralFromUrl } from './lib/referral';
import { usePresence } from './hooks/usePresence';

const APP_ENTERED_KEY = 'eleven_games_app_entered';

const appTheme = createTheme({
  palette: {
    primary: {
      main: '#ef6668',
    },
    background: {
      default: '#f4f5fb',
    },
  },
  shape: {
    borderRadius: 14,
  },
  typography: {
    fontFamily: "'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
});

function App() {
  const [hasEnteredApp, setHasEnteredApp] = useState(
    () => sessionStorage.getItem(APP_ENTERED_KEY) === '1',
  );
  const [isSplashVisible, setIsSplashVisible] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeGame, setActiveGame] = useState(null);
  const [ludoRoomId, setLudoRoomId] = useState(null);
  const [fairAviatorEntryFee, setFairAviatorEntryFee] = useState(null);
  const [pendingDepositAmount, setPendingDepositAmount] = useState(100);
  const [depositSource, setDepositSource] = useState('wallet');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');

  const loadBankAccounts = () => {
    if (!localStorage.getItem('eleven_games_token')) return Promise.resolve();
    return walletApi
      .bankAccounts()
      .then((data) => {
        const accounts = data.accounts ?? [];
        setBankAccounts(accounts);
        const defaultAccount = accounts.find((a) => a.isDefault) || accounts[0];
        if (defaultAccount) setSelectedBankId(defaultAccount.id);
        return accounts;
      })
      .catch(() => []);
  };

  const refreshUser = useCallback((user) => {
    if (user?.id) {
      setCurrentUser(user);
      return Promise.resolve(user);
    }
    return authApi.me().then((me) => {
      setCurrentUser(me);
      return me;
    });
  }, []);

  useEffect(() => {
    if (!hasEnteredApp) return undefined;

    setIsSplashVisible(true);
    const splashTimer = setTimeout(() => {
      setIsSplashVisible(false);
    }, 1800);

    return () => clearTimeout(splashTimer);
  }, [hasEnteredApp]);

  const handleEnterApp = () => {
    sessionStorage.setItem(APP_ENTERED_KEY, '1');
    setHasEnteredApp(true);
  };

  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  useEffect(() => {
    const stored = authApi.getStoredUser();
    if (stored && localStorage.getItem('eleven_games_token')) {
      authApi
        .me()
        .then((user) => {
          setCurrentUser(user);
          setIsAuthenticated(true);
          loadBankAccounts();
        })
        .catch(() => {
          authApi.logout();
        });
    }
  }, []);

  const handleLoginSuccess = (user) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    loadBankAccounts();
  };

  const handleLogout = () => {
    authApi.logout();
    resetSocket();
    setCurrentUser(null);
    setIsAuthenticated(false);
    setActiveGame(null);
    setLudoRoomId(null);
    setFairAviatorEntryFee(null);
  };

  usePresence({
    enabled: isAuthenticated && !isSplashVisible,
    game: activeGame || 'dashboard',
  });

  if (!hasEnteredApp) {
    return (
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <div className="landing-shell">
          <LandingScreen onEnterApp={handleEnterApp} />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <div className="app-root">
        <div className="mobile-frame">
          {isSplashVisible ? (
            <SplashScreen />
          ) : activeGame === 'fairaviator' && fairAviatorEntryFee ? (
            <FairAviatorScreen
              user={currentUser}
              entryFee={fairAviatorEntryFee}
              onBack={() => setFairAviatorEntryFee(null)}
              onWalletUpdate={setCurrentUser}
            />
          ) : activeGame === 'fairaviator' ? (
            <FairAviatorLobbyScreen
              onBack={() => setActiveGame(null)}
              onSelectTier={(fee) => setFairAviatorEntryFee(fee)}
            />
          ) : activeGame === 'aviator' ? (
            <AviatorScreen user={currentUser} onBack={() => setActiveGame(null)} onWalletUpdate={setCurrentUser} />
          ) : activeGame === 'wingo' ? (
            <WinGoScreen user={currentUser} onBack={() => setActiveGame(null)} onWalletUpdate={setCurrentUser} />
          ) : activeGame === 'luckydraw' ? (
            <LuckyDrawScreen user={currentUser} onBack={() => setActiveGame(null)} onWalletUpdate={setCurrentUser} />
          ) : activeGame === 'matka' ? (
            <MatkaScreen user={currentUser} onBack={() => setActiveGame(null)} onWalletUpdate={setCurrentUser} />
          ) : activeGame === 'mines' ? (
            <MinesScreen user={currentUser} onBack={() => setActiveGame(null)} onWalletUpdate={setCurrentUser} />
          ) : activeGame === 'plinko' ? (
            <PlinkoScreen user={currentUser} onBack={() => setActiveGame(null)} onWalletUpdate={setCurrentUser} />
          ) : activeGame === 'limbo' ? (
            <LimboScreen user={currentUser} onBack={() => setActiveGame(null)} onWalletUpdate={setCurrentUser} />
          ) : activeGame === 'ludo' && ludoRoomId ? (
            <LudoPvPScreen
              user={currentUser}
              roomId={ludoRoomId}
              onBack={() => {
                setLudoRoomId(null);
                setActiveGame('ludo');
              }}
              onWalletUpdate={refreshUser}
            />
          ) : activeGame === 'ludo' ? (
            <LudoLobbyScreen
              user={currentUser}
              onBack={() => setActiveGame(null)}
              onStartGame={(roomId) => setLudoRoomId(roomId)}
              onWalletUpdate={refreshUser}
            />
          ) : activeGame === 'account' ? (
            <AccountScreen
              user={currentUser}
              onOpenHome={() => setActiveGame(null)}
              onOpenWallet={() => setActiveGame('wallet')}
              onOpenWithdraw={() => setActiveGame('withdraw')}
              onOpenDeposit={() => {
                setDepositSource('account');
                setActiveGame('deposit');
              }}
              onOpenContact={() => setActiveGame('contact')}
              onLogout={handleLogout}
            />
          ) : activeGame === 'contact' ? (
            <ContactUsScreen onBack={() => setActiveGame('account')} />
          ) : activeGame === 'wallet' ? (
            <WalletScreen
              user={currentUser}
              onBack={() => setActiveGame('account')}
              onOpenDeposit={() => {
                setDepositSource('wallet');
                setActiveGame('deposit');
              }}
              onOpenWithdraw={() => setActiveGame('withdraw')}
            />
          ) : activeGame === 'deposit' ? (
            <DepositScreen
              user={currentUser}
              onBack={() => setActiveGame(depositSource === 'wallet' ? 'wallet' : 'account')}
              onOpenUpi={(amount) => {
                setPendingDepositAmount(amount);
                setActiveGame('upi');
              }}
            />
          ) : activeGame === 'upi' ? (
            <UpiPaymentScreen
              amount={pendingDepositAmount}
              onBack={() => setActiveGame('deposit')}
              onSubmitted={() => {
                authApi.me().then(setCurrentUser).catch(() => {});
              }}
            />
          ) : activeGame === 'withdraw' ? (
            <WithdrawScreen
              user={currentUser}
              onBack={() => setActiveGame('account')}
              onOpenAddBank={() => setActiveGame('bank-accounts')}
              bankAccounts={bankAccounts}
              selectedBankId={selectedBankId}
              onWalletUpdate={refreshUser}
            />
          ) : activeGame === 'bank-accounts' ? (
            <BankAccountsScreen
              onBack={() => setActiveGame('withdraw')}
              onOpenAddBank={() => setActiveGame('add-bank')}
              bankAccounts={bankAccounts}
              selectedBankId={selectedBankId}
              onSelectBank={setSelectedBankId}
              onAccountsChange={loadBankAccounts}
            />
          ) : activeGame === 'add-bank' ? (
            <AddBankAccountScreen
              onBack={() => setActiveGame('bank-accounts')}
              onSave={() => {
                loadBankAccounts().then(() => setActiveGame('bank-accounts'));
              }}
            />
          ) : isAuthenticated ? (
            <DashboardScreen
              user={currentUser}
              onOpenFairAviator={() => {
                setFairAviatorEntryFee(null);
                setActiveGame('fairaviator');
              }}
              onOpenAviator={() => setActiveGame('aviator')}
              onOpenWinGo={() => setActiveGame('wingo')}
              onOpenLuckyDraw={() => setActiveGame('luckydraw')}
              onOpenMatka={() => setActiveGame('matka')}
              onOpenMines={() => setActiveGame('mines')}
              onOpenPlinko={() => setActiveGame('plinko')}
              onOpenLimbo={() => setActiveGame('limbo')}
              onOpenLudo={() => setActiveGame('ludo')}
              onLudoMatchFound={(roomId) => {
                setActiveGame('ludo');
                setLudoRoomId(roomId);
              }}
              // onOpenDragonTiger={() => setActiveGame('dragontiger')}
              onOpenAccount={() => setActiveGame('account')}
            />
          ) : (
            <AuthScreen onLoginSuccess={handleLoginSuccess} />
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}

export default App;
